import { useInfiniteQuery } from "@tanstack/react-query";
import {
  clientService,
  ClientFilters,
  ClientResult,
} from "../api/services/clientService";
import type { Client } from "../schemas/clientSchema";
import { useClients } from "./useClients";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type ClientsInfiniteFilters = Omit<ClientFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useClientsInfinite = (filters: ClientsInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useClients(undefined, { enabled: false });

  const query = useInfiniteQuery<ClientResult, Error>({
    queryKey: ["clients", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      clientService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const clients: Client[] = query.data?.pages.flatMap((p) => p.clients) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    clients,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
